import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';
import { useNavigate } from 'react-router-dom';
import {
  createEvent,
  updateEvent,
  deleteEvent,
  getEvents
} from '../firebase/calendarService';
import { getAssignments } from '../firebase/assignmentService';
import { getLessons } from '../firebase/lessonPlannerService';
import {
  startOfMonth,
  endOfMonth,
  startOfWeek,
  endOfWeek,
  addDays,
  addMonths,
  format,
  isSameMonth,
  isSameDay
} from 'date-fns';
import './Calendar.css';

const EVENT_COLORS = [
  '#2563eb', // bright blue
  '#dc2626', // bright red
  '#059669', // bright green
  '#d97706', // bright orange
  '#7c3aed', // bright purple
  '#db2777', // bright pink
  '#0891b2', // cyan
  '#4f46e5', // indigo
];

const EVENT_TYPES = {
  'meeting': { label: 'Meeting', color: '#3b82f6' },
  'appointment': { label: 'Appointment', color: '#10b981' },
  'task': { label: 'Task', color: '#f59e0b' },
  'reminder': { label: 'Reminder', color: '#ef4444' },
  'personal': { label: 'Personal', color: '#8b5cf6' },
  'work': { label: 'Work', color: '#06b6d4' },
  'tfa-tournament': { label: 'TFA Tournament', color: '#dc2626' },
  'uil-tournament': { label: 'UIL Tournament', color: '#2563eb' },
  'nsda-tournament': { label: 'NSDA Tournament', color: '#059669' },
  'other': { label: 'Other', color: '#6b7280' },
};

function Calendar() {
  const { currentUser, logout } = useAuth();
  const { isDarkMode, toggleDarkMode } = useTheme();
  const navigate = useNavigate();
  const [currentDate, setCurrentDate] = useState(new Date());
  const [events, setEvents] = useState([]);
  const [assignments, setAssignments] = useState([]);
  const [lessons, setLessons] = useState([]);
  const [showEventModal, setShowEventModal] = useState(false);
  const [editingEvent, setEditingEvent] = useState(null);
  const [viewMode, setViewMode] = useState('month'); // 'month', 'fall', 'spring', 'list'
  const [showEvents, setShowEvents] = useState(true);
  const [showLessons, setShowLessons] = useState(true);
  const [showAssignments, setShowAssignments] = useState(true);
  const [eventForm, setEventForm] = useState({
    title: '',
    startDate: '',
    startTime: '',
    endDate: '',
    endTime: '',
    description: '',
    color: EVENT_COLORS[0],
    type: 'other',
    tournamentUrl: '',
    scheduleUrl: ''
  });
  const [loading, setLoading] = useState(true);

  const loadEvents = useCallback(async () => {
    setLoading(true);
    const [eventsResult, assignmentsResult, lessonsResult] = await Promise.all([
      getEvents(currentUser?.uid || 'admin'),
      currentUser ? getAssignments(currentUser.uid) : Promise.resolve({ success: true, assignments: [] }),
      currentUser ? getLessons(currentUser.uid) : Promise.resolve({ success: true, lessons: [] })
    ]);

    if (eventsResult.success) {
      setEvents(eventsResult.events);
    }
    if (assignmentsResult.success) {
      setAssignments(assignmentsResult.assignments);
    }
    if (lessonsResult.success) {
      setLessons(lessonsResult.lessons);
    }
    setLoading(false);
  }, [currentUser]);

  useEffect(() => {
    loadEvents();
  }, [loadEvents]);

  const handlePrevMonth = () => {
    setCurrentDate(addMonths(currentDate, -1));
  };

  const handleNextMonth = () => {
    setCurrentDate(addMonths(currentDate, 1));
  };

  const handleToday = () => {
    setCurrentDate(new Date());
  };

  const handleDateClick = (date) => {
    if (!currentUser) {
      alert('Please login to create events');
      return;
    }
    const dateStr = format(date, 'yyyy-MM-dd');
    setEventForm({
      title: '',
      startDate: dateStr,
      startTime: '09:00',
      endDate: dateStr,
      endTime: '10:00',
      description: '',
      color: EVENT_COLORS[0],
      type: 'other',
      tournamentUrl: '',
      scheduleUrl: ''
    });
    setShowEventModal(true);
    setEditingEvent(null);
  };

  const handleCreateEvent = () => {
    if (!currentUser) {
      alert('Please login to create events');
      return;
    }
    const today = new Date();
    const dateStr = format(today, 'yyyy-MM-dd');
    setEventForm({
      title: '',
      startDate: dateStr,
      startTime: '09:00',
      endDate: dateStr,
      endTime: '10:00',
      description: '',
      color: EVENT_COLORS[0],
      type: 'other',
      tournamentUrl: '',
      scheduleUrl: ''
    });
    setShowEventModal(true);
    setEditingEvent(null);
  };

  const handleEventClick = (event, e) => {
    e.stopPropagation();

    // Convert stored date/time back to the format needed by the form
    let startDate, startTime, endDate, endTime;

    if (event.startTime && typeof event.startTime === 'object' && event.startTime.toDate) {
      // Firestore Timestamp
      const start = event.startTime.toDate();
      const end = event.endTime.toDate();
      startDate = format(start, 'yyyy-MM-dd');
      startTime = format(start, 'HH:mm');
      endDate = format(end, 'yyyy-MM-dd');
      endTime = format(end, 'HH:mm');
    } else if (event.date) {
      // Format with separate date and time fields (from getEvents conversion)
      startDate = event.date;
      startTime = event.startTime || '09:00';
      endDate = event.endDate || event.date; // Use endDate if available
      endTime = event.endTime || '10:00';
    }

    if (!currentUser) {
      // Show read-only view for non-logged-in users
      setEditingEvent({
        ...event,
        startDate,
        startTime,
        endDate,
        endTime
      });
      setEventForm({
        title: event.title,
        startDate,
        startTime,
        endDate,
        endTime,
        description: event.description || '',
        color: event.color || EVENT_COLORS[0],
        type: event.type || 'other',
        tournamentUrl: event.tournamentUrl || '',
        scheduleUrl: event.scheduleUrl || ''
      });
      setShowEventModal(true);
      return;
    }

    setEditingEvent({
      ...event,
      startDate,
      startTime,
      endDate,
      endTime
    });
    setEventForm({
      title: event.title,
      startDate,
      startTime,
      endDate,
      endTime,
      description: event.description || '',
      color: event.color || EVENT_COLORS[0],
      type: event.type || 'other',
      tournamentUrl: event.tournamentUrl || '',
      scheduleUrl: event.scheduleUrl || ''
    });
    setShowEventModal(true);
  };

  const handleSaveEvent = async (e) => {
    e.preventDefault();
    if (!eventForm.title || !eventForm.startDate) return;

    const eventData = {
      title: eventForm.title,
      date: eventForm.startDate,
      startTime: eventForm.startTime,
      endDate: eventForm.endDate,
      endTime: eventForm.endTime,
      description: eventForm.description,
      color: eventForm.color,
      type: eventForm.type,
      tournamentUrl: eventForm.tournamentUrl || '',
      scheduleUrl: eventForm.scheduleUrl || ''
    };

    if (editingEvent) {
      const result = await updateEvent(currentUser.uid, editingEvent.id, eventData);
      if (result.success) {
        await loadEvents();
        closeModal();
      }
    } else {
      const result = await createEvent(currentUser.uid, eventData);
      if (result.success) {
        await loadEvents();
        closeModal();
      }
    }
  };

  const handleDeleteEvent = async () => {
    if (!editingEvent) return;
    if (window.confirm('Are you sure you want to delete this event?')) {
      const result = await deleteEvent(currentUser.uid, editingEvent.id);
      if (result.success) {
        await loadEvents();
        closeModal();
      }
    }
  };

  const closeModal = () => {
    setShowEventModal(false);
    setEditingEvent(null);
    setEventForm({
      title: '',
      startDate: '',
      startTime: '',
      endDate: '',
      endTime: '',
      tournamentUrl: '',
      scheduleUrl: '',
      description: '',
      color: EVENT_COLORS[0],
      type: 'other'
    });
  };

  const handleExport = () => {
    const dataStr = JSON.stringify(events, null, 2);
    const dataUri = 'data:application/json;charset=utf-8,' + encodeURIComponent(dataStr);
    const exportFileDefaultName = `calendar-events-${new Date().toISOString().split('T')[0]}.json`;

    const linkElement = document.createElement('a');
    linkElement.setAttribute('href', dataUri);
    linkElement.setAttribute('download', exportFileDefaultName);
    linkElement.click();
  };

  const getEventsForDate = (date) => {
    const dateStr = format(date, 'yyyy-MM-dd');
    return events.filter(event => {
      let startDateStr, endDateStr;

      // Check if event has Timestamp objects (original format)
      if (event.startTime && typeof event.startTime === 'object' && event.startTime.toDate) {
        const startDateTime = event.startTime.toDate();
        const endDateTime = event.endTime.toDate();
        startDateStr = format(startDateTime, 'yyyy-MM-dd');
        endDateStr = format(endDateTime, 'yyyy-MM-dd');
      }
      // Otherwise use the converted date/endDate fields
      else if (event.date) {
        startDateStr = event.date;
        endDateStr = event.endDate || event.date;
      } else {
        return false;
      }

      // Check if the date falls within the event's date range (inclusive)
      return dateStr >= startDateStr && dateStr <= endDateStr;
    });
  };

  const getAssignmentsForDate = (date) => {
    const dateStr = format(date, 'yyyy-MM-dd');
    return assignments.filter(assignment => {
      if (!assignment.assignmentDate) return false;
      const assignmentDateStr = assignment.assignmentDate.toDate ?
        format(assignment.assignmentDate.toDate(), 'yyyy-MM-dd') :
        assignment.assignmentDate;
      return assignmentDateStr === dateStr;
    });
  };

  const getLessonsForDate = (date) => {
    const dateStr = format(date, 'yyyy-MM-dd');
    return lessons.filter(lesson => {
      // Check both startDate and scheduledDate
      const dateField = lesson.startDate || lesson.scheduledDate;
      if (!dateField) return false;

      const lessonDateStr = dateField.toDate ?
        format(dateField.toDate(), 'yyyy-MM-dd') :
        dateField;
      return lessonDateStr === dateStr;
    });
  };

  const getEventsForMonth = (monthDate) => {
    const monthStart = startOfMonth(monthDate);
    const monthEnd = endOfMonth(monthDate);
    const monthStartStr = format(monthStart, 'yyyy-MM-dd');
    const monthEndStr = format(monthEnd, 'yyyy-MM-dd');

    return events.filter(event => {
      let startDateStr, endDateStr;

      if (event.startTime && typeof event.startTime === 'object' && event.startTime.toDate) {
        const startDateTime = event.startTime.toDate();
        const endDateTime = event.endTime.toDate();
        startDateStr = format(startDateTime, 'yyyy-MM-dd');
        endDateStr = format(endDateTime, 'yyyy-MM-dd');
      } else if (event.date) {
        startDateStr = event.date;
        endDateStr = event.endDate || event.date;
      } else {
        return false;
      }

      // Event overlaps with this month if:
      // - Event starts before month ends AND event ends after month starts
      return startDateStr <= monthEndStr && endDateStr >= monthStartStr;
    }).sort((a, b) => {
      // Sort by start date
      const aDate = a.date || format(a.startTime.toDate(), 'yyyy-MM-dd');
      const bDate = b.date || format(b.startTime.toDate(), 'yyyy-MM-dd');
      return aDate.localeCompare(bDate);
    });
  };

  const renderSingleMonth = (monthDate, isCompact = false) => {
    const monthStart = startOfMonth(monthDate);
    const monthEnd = endOfMonth(monthStart);
    const startDate = startOfWeek(monthStart);
    const endDate = endOfWeek(monthEnd);

    const rows = [];
    let days = [];
    let day = startDate;

    while (day <= endDate) {
      for (let i = 0; i < 7; i++) {
        const formattedDate = format(day, 'd');
        const cloneDay = day;
        const allDayEvents = showEvents ? getEventsForDate(day) : [];
        const allDayAssignments = showAssignments ? getAssignmentsForDate(day) : [];
        const allDayLessons = showLessons ? getLessonsForDate(day) : [];

        const totalItems = allDayEvents.length + allDayAssignments.length + allDayLessons.length;
        const maxVisible = isCompact ? 3 : 6;

        days.push(
          <div
            className={`calendar-day ${isCompact ? 'compact' : ''} ${
              !isSameMonth(day, monthStart) ? 'disabled' : ''
            } ${isSameDay(day, new Date()) ? 'today' : ''} ${isDarkMode ? 'dark' : ''}`}
            key={day}
            onClick={() => handleDateClick(cloneDay)}
          >
            <span className="day-number">{formattedDate}</span>
            <div className="day-events">
              {allDayEvents.slice(0, isCompact ? 1 : 2).map(event => (
                <div
                  key={event.id}
                  className="event-pill"
                  style={{ backgroundColor: event.color || EVENT_COLORS[0] }}
                  onClick={(e) => handleEventClick(event, e)}
                >
                  {event.title}
                </div>
              ))}
              {allDayLessons.slice(0, isCompact ? 1 : 2).map(lesson => (
                <div
                  key={lesson.id}
                  className="event-pill lesson-pill"
                  style={{ backgroundColor: '#10b981', borderLeft: '4px solid #059669' }}
                  title={`Lesson: ${lesson.title}`}
                >
                  📚 {lesson.title}
                </div>
              ))}
              {allDayAssignments.slice(0, isCompact ? 1 : 2).map(assignment => (
                <div
                  key={assignment.id}
                  className="event-pill assignment-pill"
                  style={{ backgroundColor: '#f59e0b', borderLeft: '4px solid #d97706' }}
                  title={`Assignment: ${assignment.title}`}
                >
                  📝 {assignment.title}
                </div>
              ))}
              {totalItems > maxVisible && (
                <div className="more-events">
                  +{totalItems - maxVisible} more
                </div>
              )}
            </div>
          </div>
        );
        day = addDays(day, 1);
      }
      rows.push(
        <div className="calendar-row" key={day}>
          {days}
        </div>
      );
      days = [];
    }
    return rows;
  };

  const renderMonthView = () => {
    const rows = renderSingleMonth(currentDate, false);
    return <div className="calendar-grid">{rows}</div>;
  };

  const renderEventListItem = (event) => {
    const eventStartDate = event.date || format(event.startTime.toDate(), 'yyyy-MM-dd');
    const eventEndDate = event.endDate || eventStartDate;
    const eventStartTime = event.startTime && typeof event.startTime === 'string' ? event.startTime : '09:00';
    const eventEndTime = event.endTime && typeof event.endTime === 'string' ? event.endTime : '10:00';

    return (
      <div
        key={event.id}
        className="event-list-item"
        style={{ borderLeftColor: event.color || EVENT_COLORS[0] }}
        onClick={(e) => handleEventClick(event, e)}
      >
        <div className="event-list-color" style={{ backgroundColor: event.color || EVENT_COLORS[0] }}></div>
        <div className="event-list-content">
          <div className="event-list-title">{event.title}</div>
          <div className="event-list-meta">
            <span className="event-list-date">
              {format(new Date(eventStartDate), 'MMM d')}
              {eventEndDate !== eventStartDate && ` - ${format(new Date(eventEndDate), 'MMM d')}`}
            </span>
            <span className="event-list-time">
              {eventStartTime} - {eventEndTime}
            </span>
            {event.type && (
              <span className="event-list-type" style={{ backgroundColor: event.color || EVENT_COLORS[0] }}>
                {EVENT_TYPES[event.type]?.label || 'Other'}
              </span>
            )}
          </div>
          {event.description && (
            <div className="event-list-description">{event.description}</div>
          )}
        </div>
      </div>
    );
  };

  const renderListView = () => {
    // Show academic year: Aug-Dec of current year, then Jan-Jul of next year
    const currentMonth = currentDate.getMonth();
    const currentYear = currentDate.getFullYear();

    // If we're in Aug-Dec (months 7-11), academic year is current year to next year
    // If we're in Jan-Jul (months 0-6), academic year is previous year to current year
    const academicYearStart = currentMonth >= 7 ? currentYear : currentYear - 1;

    // Create months array: Aug-Dec of start year (7-11), then Jan-Jul of next year (0-6)
    const monthsToShow = [
      ...Array.from({ length: 5 }, (_, i) => ({ month: i + 7, year: academicYearStart })), // Aug-Dec
      ...Array.from({ length: 7 }, (_, i) => ({ month: i, year: academicYearStart + 1 }))  // Jan-Jul
    ];

    return (
      <div className="list-view">
        {monthsToShow.map(({ month, year }, index) => {
          const monthDate = new Date(year, month, 1);
          const monthEvents = getEventsForMonth(monthDate);

          if (monthEvents.length === 0) return null;

          return (
            <div key={index} className="list-view-month">
              <div className="list-view-month-header">
                <h3>{format(monthDate, 'MMMM yyyy')}</h3>
                <span className="event-count">{monthEvents.length} event{monthEvents.length !== 1 ? 's' : ''}</span>
              </div>
              <div className="event-list">
                {monthEvents.map(event => renderEventListItem(event))}
              </div>
            </div>
          );
        })}
        {events.length === 0 && (
          <div className="no-events-message">
            <p>No events found for academic year {academicYearStart}-{academicYearStart + 1}</p>
          </div>
        )}
      </div>
    );
  };

  const renderSemesterView = (semesterMonths, semesterType) => {
    const currentMonth = currentDate.getMonth();
    const currentYear = currentDate.getFullYear();

    // Determine the correct year for the semester
    let semesterYear;
    if (semesterType === 'fall') {
      // Fall is Aug-Dec: use current year if we're in Aug-Dec, previous year if we're in Jan-Jul
      semesterYear = currentMonth >= 7 ? currentYear : currentYear - 1;
    } else {
      // Spring is Jan-May: use next year if we're in Aug-Dec, current year if we're in Jan-Jul
      semesterYear = currentMonth >= 7 ? currentYear + 1 : currentYear;
    }

    return (
      <div className="semester-view">
        {semesterMonths.map((monthOffset, idx) => {
          const monthDate = new Date(semesterYear, monthOffset, 1);
          const monthEvents = getEventsForMonth(monthDate);

          return (
            <div key={idx} className="semester-month">
              <div className="semester-month-header">
                <h3>{format(monthDate, 'MMMM yyyy')}</h3>
                <span className="event-count">{monthEvents.length} event{monthEvents.length !== 1 ? 's' : ''}</span>
              </div>
              <div className="calendar-weekdays compact">
                <div className="weekday">S</div>
                <div className="weekday">M</div>
                <div className="weekday">T</div>
                <div className="weekday">W</div>
                <div className="weekday">T</div>
                <div className="weekday">F</div>
                <div className="weekday">S</div>
              </div>
              <div className="calendar-grid compact">
                {renderSingleMonth(monthDate, true)}
              </div>
              {monthEvents.length > 0 && (
                <div className="semester-events-list">
                  <div className="semester-events-header">Events this month:</div>
                  {monthEvents.map(event => renderEventListItem(event))}
                </div>
              )}
            </div>
          );
        })}
      </div>
    );
  };

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  if (loading) {
    return (
      <div className={`calendar-container ${isDarkMode ? 'dark' : ''}`}>
        <div className="loading-container">
          <div className="loading-spinner"></div>
          <p>Loading calendar...</p>
        </div>
      </div>
    );
  }

  return (
    <div className={`calendar-container ${isDarkMode ? 'dark' : ''}`}>
      <header className="calendar-header">
        <div className="calendar-header-wrapper">
          <div className="calendar-header-row-1">
            <button onClick={() => navigate('/')} className="back-button">
              ← Home
            </button>
            <h1>Mr.Willeby's Calendar</h1>
            <div className="header-controls-top">
              <button onClick={toggleDarkMode} className="dark-mode-button" title={isDarkMode ? 'Switch to Light Mode' : 'Switch to Dark Mode'}>
                {isDarkMode ? '☀️' : '🌙'}
              </button>
              <button onClick={handleExport} className="export-button" title="Export events to file">
                📤
              </button>
              <button onClick={() => window.print()} className="print-button" title="Print calendar">
                🖨️
              </button>
              {currentUser ? (
                <button onClick={handleLogout} className="logout-button-small">Logout</button>
              ) : (
                <button onClick={() => navigate('/')} className="login-button">Login</button>
              )}
            </div>
          </div>
          <div className="calendar-header-row-2">
            <div className="header-navigation">
              {viewMode === 'month' && (
                <>
                  <button onClick={handlePrevMonth} className="nav-button">←</button>
                  <h2>{format(currentDate, 'MMMM yyyy')}</h2>
                  <button onClick={handleNextMonth} className="nav-button">→</button>
                </>
              )}
              {viewMode === 'fall' && (
                <h2>Fall Semester {currentDate.getMonth() >= 7 ? currentDate.getFullYear() : currentDate.getFullYear() - 1}</h2>
              )}
              {viewMode === 'spring' && (
                <h2>Spring Semester {currentDate.getMonth() >= 7 ? currentDate.getFullYear() + 1 : currentDate.getFullYear()}</h2>
              )}
              {viewMode === 'list' && (
                <h2>Academic Year {currentDate.getMonth() >= 7 ? currentDate.getFullYear() : currentDate.getFullYear() - 1}-{currentDate.getMonth() >= 7 ? currentDate.getFullYear() + 1 : currentDate.getFullYear()}</h2>
              )}
              <button onClick={handleToday} className="today-button">Today</button>
              <button onClick={handleCreateEvent} className="create-event-button">+ Create Event</button>
            </div>
          </div>
          <div className="calendar-header-row-3">
            <div className="view-selector">
              <button
                onClick={() => setViewMode('month')}
                className={`view-button ${viewMode === 'month' ? 'active' : ''}`}
              >
                Month
              </button>
              <button
                onClick={() => setViewMode('fall')}
                className={`view-button ${viewMode === 'fall' ? 'active' : ''}`}
              >
                Fall Semester
              </button>
              <button
                onClick={() => setViewMode('spring')}
                className={`view-button ${viewMode === 'spring' ? 'active' : ''}`}
              >
                Spring Semester
              </button>
              <button
                onClick={() => setViewMode('list')}
                className={`view-button ${viewMode === 'list' ? 'active' : ''}`}
              >
                List View
              </button>
            </div>
            <div className="filter-toggles" style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
              <span style={{ fontSize: '0.875rem', fontWeight: 600, marginRight: '0.5rem' }}>Show:</span>
              <button
                onClick={() => setShowEvents(!showEvents)}
                className={`filter-toggle ${showEvents ? 'active' : ''}`}
                style={{
                  padding: '0.4rem 0.8rem',
                  borderRadius: '6px',
                  border: showEvents ? '2px solid #3b82f6' : '2px solid #e5e7eb',
                  background: showEvents ? '#eff6ff' : 'white',
                  color: showEvents ? '#1e40af' : '#6b7280',
                  fontSize: '0.875rem',
                  cursor: 'pointer',
                  fontWeight: 500,
                  transition: 'all 0.2s'
                }}
              >
                📅 Events
              </button>
              <button
                onClick={() => setShowLessons(!showLessons)}
                className={`filter-toggle ${showLessons ? 'active' : ''}`}
                style={{
                  padding: '0.4rem 0.8rem',
                  borderRadius: '6px',
                  border: showLessons ? '2px solid #10b981' : '2px solid #e5e7eb',
                  background: showLessons ? '#d1fae5' : 'white',
                  color: showLessons ? '#065f46' : '#6b7280',
                  fontSize: '0.875rem',
                  cursor: 'pointer',
                  fontWeight: 500,
                  transition: 'all 0.2s'
                }}
              >
                📚 Lessons
              </button>
              <button
                onClick={() => setShowAssignments(!showAssignments)}
                className={`filter-toggle ${showAssignments ? 'active' : ''}`}
                style={{
                  padding: '0.4rem 0.8rem',
                  borderRadius: '6px',
                  border: showAssignments ? '2px solid #f59e0b' : '2px solid #e5e7eb',
                  background: showAssignments ? '#fef3c7' : 'white',
                  color: showAssignments ? '#92400e' : '#6b7280',
                  fontSize: '0.875rem',
                  cursor: 'pointer',
                  fontWeight: 500,
                  transition: 'all 0.2s'
                }}
              >
                📝 Assignments
              </button>
            </div>
          </div>
        </div>
      </header>

      {viewMode === 'month' && (
        <div className="calendar-weekdays">
          <div className="weekday">Sun</div>
          <div className="weekday">Mon</div>
          <div className="weekday">Tue</div>
          <div className="weekday">Wed</div>
          <div className="weekday">Thu</div>
          <div className="weekday">Fri</div>
          <div className="weekday">Sat</div>
        </div>
      )}

      {viewMode === 'month' && renderMonthView()}
      {viewMode === 'fall' && renderSemesterView([7, 8, 9, 10, 11], 'fall')} {/* Aug-Dec */}
      {viewMode === 'spring' && renderSemesterView([0, 1, 2, 3, 4], 'spring')} {/* Jan-May */}
      {viewMode === 'list' && renderListView()}

      {showEventModal && (
        <div className="modal-overlay" onClick={closeModal}>
          <div className={`modal-content ${isDarkMode ? 'dark' : ''}`} onClick={(e) => e.stopPropagation()}>
            <button className="modal-close" onClick={closeModal}>×</button>

            {!currentUser && editingEvent ? (
              // Read-only view for non-logged-in users
              <>
                <div className="color-bar" style={{ backgroundColor: eventForm.color }}></div>
                <h2>{eventForm.title}</h2>
                <div className="event-type-badge" style={{ backgroundColor: eventForm.color }}>
                  {EVENT_TYPES[eventForm.type]?.label || 'Other'}
                </div>

                <div className="event-time-info">
                  <div className="time-block">
                    <div className="time-label">START</div>
                    <div className="time-date">{format(new Date(`${eventForm.startDate}T${eventForm.startTime}`), 'MMM d, yyyy')}</div>
                    <div className="time-time">{format(new Date(`${eventForm.startDate}T${eventForm.startTime}`), 'h:mm a')}</div>
                  </div>
                  <div className="time-block">
                    <div className="time-label">END</div>
                    <div className="time-date">{format(new Date(`${eventForm.endDate}T${eventForm.endTime}`), 'MMM d, yyyy')}</div>
                    <div className="time-time">{format(new Date(`${eventForm.endDate}T${eventForm.endTime}`), 'h:mm a')}</div>
                  </div>
                </div>

                {eventForm.description && (
                  <div className="event-description-display">
                    <div className="description-label">Description</div>
                    <div className="description-text">{eventForm.description}</div>
                  </div>
                )}

                <div className="modal-buttons">
                  <button type="button" onClick={closeModal} className="cancel-button">
                    Close
                  </button>
                </div>
              </>
            ) : (
              // Edit form for logged-in users
              <>
                <h2>{editingEvent ? 'Edit Event' : 'New Event'}</h2>

                <form onSubmit={handleSaveEvent}>
                  <div className="form-group">
                    <label htmlFor="title">Event Title</label>
                    <input
                      type="text"
                      id="title"
                      value={eventForm.title}
                      onChange={(e) => setEventForm({ ...eventForm, title: e.target.value })}
                      placeholder="Enter event title"
                      required
                      disabled={!currentUser}
                    />
                  </div>

                  <div className="form-group">
                    <label htmlFor="type">Event Type</label>
                    <select
                      id="type"
                      value={eventForm.type}
                      onChange={(e) => {
                        const type = e.target.value;
                        setEventForm({ ...eventForm, type, color: EVENT_TYPES[type].color });
                      }}
                      disabled={!currentUser}
                    >
                      {Object.entries(EVENT_TYPES).map(([value, { label }]) => (
                        <option key={value} value={value}>{label}</option>
                      ))}
                    </select>
                  </div>

                  <div className="form-row">
                    <div className="form-group">
                      <label htmlFor="startDate">Start Date</label>
                      <input
                        type="date"
                        id="startDate"
                        value={eventForm.startDate}
                        onChange={(e) => setEventForm({ ...eventForm, startDate: e.target.value })}
                        required
                        disabled={!currentUser}
                      />
                    </div>

                    <div className="form-group">
                      <label htmlFor="startTime">Start Time</label>
                      <input
                        type="time"
                        id="startTime"
                        value={eventForm.startTime}
                        onChange={(e) => setEventForm({ ...eventForm, startTime: e.target.value })}
                        required
                        disabled={!currentUser}
                      />
                    </div>
                  </div>

                  <div className="form-row">
                    <div className="form-group">
                      <label htmlFor="endDate">End Date</label>
                      <input
                        type="date"
                        id="endDate"
                        value={eventForm.endDate}
                        onChange={(e) => setEventForm({ ...eventForm, endDate: e.target.value })}
                        required
                        disabled={!currentUser}
                      />
                    </div>

                    <div className="form-group">
                      <label htmlFor="endTime">End Time</label>
                      <input
                        type="time"
                        id="endTime"
                        value={eventForm.endTime}
                        onChange={(e) => setEventForm({ ...eventForm, endTime: e.target.value })}
                        required
                        disabled={!currentUser}
                      />
                    </div>
                  </div>

                  <div className="form-group">
                    <label>Color</label>
                    <div className="color-picker">
                      {EVENT_COLORS.map((color) => (
                        <button
                          key={color}
                          type="button"
                          className={`color-option ${eventForm.color === color ? 'selected' : ''}`}
                          style={{ backgroundColor: color }}
                          onClick={() => !(!currentUser) && setEventForm({ ...eventForm, color })}
                          disabled={!currentUser}
                        />
                      ))}
                    </div>
                  </div>

                  <div className="form-group">
                    <label htmlFor="description">Description</label>
                    <textarea
                      id="description"
                      value={eventForm.description}
                      onChange={(e) => setEventForm({ ...eventForm, description: e.target.value })}
                      placeholder="Event description (optional)"
                      rows="3"
                      disabled={!currentUser}
                    />
                  </div>

                  {(eventForm.type === 'tfa-tournament' || eventForm.type === 'uil-tournament' || eventForm.type === 'nsda-tournament') && (
                    <>
                      <div className="form-group">
                        <label htmlFor="tournamentUrl">Tournament URL</label>
                        <input
                          type="url"
                          id="tournamentUrl"
                          value={eventForm.tournamentUrl}
                          onChange={(e) => setEventForm({ ...eventForm, tournamentUrl: e.target.value })}
                          placeholder="https://example.com/tournament"
                          disabled={!currentUser}
                        />
                      </div>

                      <div className="form-group">
                        <label htmlFor="scheduleUrl">Schedule URL</label>
                        <input
                          type="url"
                          id="scheduleUrl"
                          value={eventForm.scheduleUrl}
                          onChange={(e) => setEventForm({ ...eventForm, scheduleUrl: e.target.value })}
                          placeholder="https://example.com/schedule"
                          disabled={!currentUser}
                        />
                      </div>
                    </>
                  )}

                  {!currentUser && (
                    <div className="login-prompt-modal">
                      Sign in to edit events
                    </div>
                  )}

                  <div className="modal-buttons">
                    {currentUser && (
                      <button type="submit" className="save-button">
                        {editingEvent ? 'Update' : 'Create'} Event
                      </button>
                    )}
                    {editingEvent && currentUser && (
                      <button
                        type="button"
                        onClick={handleDeleteEvent}
                        className="delete-button"
                      >
                        Delete
                      </button>
                    )}
                    <button type="button" onClick={closeModal} className="cancel-button">
                      {currentUser ? 'Cancel' : 'Close'}
                    </button>
                  </div>
                </form>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export default Calendar;
